import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Task, Guest } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { SearchAndFilter } from '../components/SearchAndFilter';
import { CheckSquare, PlusCircle, Calendar, Trash2, Edit2, CheckCircle2, Circle, User, AlertCircle, Clock } from 'lucide-react';
import { PHASES } from '../constants';
import { Button } from '../components/ui/Button';

const INITIAL_TASK: Partial<Task> = {
  title: '',
  description: '',
  phase: 'early',
  status: 'todo',
  assigned_to: ''
};

export default function Tasks() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Task>>(INITIAL_TASK);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [filterTime, setFilterTime] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchData = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const [tRes, gRes] = await Promise.all([
      supabase.from('tasks').select('*').eq('wedding_id', wedding.id).order('due_date', { ascending: true }),
      supabase.from('guests').select('*').eq('wedding_id', wedding.id).order('full_name')
    ]);
    
    if (tRes.data) setTasks(tRes.data);
    if (gRes.data) setGuests((gRes.data as Guest[]).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
  };

  useEffect(() => {
    fetchData();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const payload = { 
      ...formData, 
      assigned_to: formData.assigned_to === '' ? null : formData.assigned_to,
      wedding_id: wedding.id 
    };

    if (formData.id) {
      const { error } = await supabase.from('tasks').update(payload).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('tasks').insert(payload);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('tasks').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchData();
  };

  const toggleStatus = async (task: Task) => {
    const newStatus = task.status === 'done' ? 'todo' : 'done';
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('tasks').update({ status: newStatus }).eq('id', task.id);
    if (error) { showToast('error', `Chyba: ${error.message}`); fetchData(); return; }
    fetchData();
  };

  const getGuestName = (id?: string) => {
    if (!id) return null;
    const guest = guests.find(g => g.id === id);
    return guest ? guest.full_name : 'Neznámý host';
  };

  // Filter Logic
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (task.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                          (filterStatus === 'done' && task.status === 'done') ||
                          (filterStatus === 'todo' && task.status !== 'done');

    const matchesAssignee = filterAssignee === 'all' ||
                            (filterAssignee === 'unassigned' && !task.assigned_to) ||
                            (filterAssignee === task.assigned_to);

    const matchesTime = (() => {
        if (filterTime === 'all') return true;
        if (!task.due_date) return filterTime === 'upcoming'; // No date = usually future/someday
        const due = new Date(task.due_date);
        const today = new Date();
        today.setHours(0,0,0,0);
        if (filterTime === 'overdue') return due < today && task.status !== 'done';
        if (filterTime === 'upcoming') return due >= today || task.status === 'done';
        return true;
    })();

    return matchesSearch && matchesStatus && matchesAssignee && matchesTime;
  });

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.tasks')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">Seznam úkolů s možností přiřazení.</p>
        </div>
        <Button 
          onClick={() => { setFormData(INITIAL_TASK); setIsModalOpen(true); }}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <SearchAndFilter searchQuery={searchQuery} onSearchChange={setSearchQuery}>
         <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-500"
         >
            <option value="all">Všechny stavy</option>
            <option value="todo">K vyřízení</option>
            <option value="done">Hotovo</option>
         </select>

         <select 
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-500 max-w-[200px]"
         >
            <option value="all">Všichni řešitelé</option>
            <option value="unassigned">{t('tasks.unassigned')}</option>
            {guests.map(g => (
               <option key={g.id} value={g.id}>{g.full_name}</option>
            ))}
         </select>

         <select 
            value={filterTime}
            onChange={(e) => setFilterTime(e.target.value)}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-500"
         >
            <option value="all">Všechny termíny</option>
            <option value="overdue">Zpožděné</option>
            <option value="upcoming">Nadcházející</option>
         </select>
      </SearchAndFilter>

      <div className="space-y-3">
        {filteredTasks.length === 0 ? (
          <EmptyState 
            icon={CheckSquare} 
            title="Žádné úkoly" 
            description="Zadaným filtrům neodpovídají žádné úkoly."
            action={{ 
              label: t('common.add'), 
              onClick: () => { setFormData(INITIAL_TASK); setIsModalOpen(true); },
              icon: PlusCircle
            }}
          />
        ) : (
          filteredTasks.map(task => {
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';
            const guestName = getGuestName(task.assigned_to);

            return (
              <div 
                key={task.id} 
                className={`
                  group bg-white dark:bg-slate-900 px-4 py-3 rounded-2xl border border-ds-border transition-all
                  ${task.status === 'done' ? 'opacity-60' : ''}
                  ${isOverdue ? 'border-l-4 border-l-red-500' : ''}
                `}
              >
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => toggleStatus(task)}
                    className={`flex-shrink-0 transition-colors ${task.status === 'done' ? 'text-green-500' : 'text-slate-300 hover:text-brand-500'}`}
                  >
                    {task.status === 'done' ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                  </button>
                  
                  <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <h3 className={`font-semibold text-sm ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                      {task.title}
                    </h3>
                    {task.due_date && (
                      <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500' : 'text-slate-500'}`}>
                        <Calendar size={12} />
                        {new Date(task.due_date).toLocaleDateString(locale)}
                        {isOverdue && <span className="bg-red-100 dark:bg-red-900/30 px-1 py-0.5 rounded text-[10px]">ZPOŽDĚNÍ</span>}
                      </span>
                    )}
                    {guestName ? (
                      <span className="flex items-center gap-1 text-xs text-brand-600 bg-brand-50 dark:bg-brand-900/10 px-2 py-0.5 rounded-lg">
                        <User size={12} /> {guestName}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-slate-400 border border-dashed border-slate-300 dark:border-slate-700 px-2 py-0.5 rounded-lg">
                        <User size={12} /> {t('tasks.unassigned')}
                      </span>
                    )}
                    <span className="text-ds-meta text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-xs">
                      {t(`timeline.phases.${task.phase}`)}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setFormData(task); setIsModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-brand-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg" aria-label={t('common.edit')}>
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => setDeleteId(task.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-slate-50 dark:hover:bg-red-900/20 rounded-lg" aria-label={t('common.delete')}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {task.description && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 ml-8 whitespace-pre-wrap line-clamp-2">{task.description}</p>
                )}
              </div>
            );
          })
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit úkol' : 'Nový úkol'}>
        <form onSubmit={handleSave} className="space-y-6">
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">Název úkolu</label>
            <input 
              required
              autoFocus
              type="text" 
              value={formData.title || ''} 
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
              placeholder="Co je potřeba zařídit?"
            />
          </div>
          
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">Popis</label>
            <textarea 
              value={formData.description || ''} 
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200 min-h-[100px]"
              placeholder="Detaily, poznámky, odkazy..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-ds-meta text-slate-500 mb-2 block">{t('tasks.assign')}</label>
              <select 
                value={formData.assigned_to || ''} 
                onChange={e => setFormData({ ...formData, assigned_to: e.target.value })}
                className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
              >
                <option value="">-- {t('common.none')} --</option>
                {guests.map(g => (
                  <option key={g.id} value={g.id}>{g.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-ds-meta text-slate-500 mb-2 block">Termín</label>
              <input 
                type="date" 
                value={formData.due_date || ''} 
                onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
             <label className="text-ds-meta text-slate-500 mb-2 block">Fáze (pro přehled)</label>
             <select 
               value={formData.phase} 
               onChange={e => setFormData({ ...formData, phase: e.target.value as any })}
               className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
             >
               {PHASES.map(p => (
                 <option key={p.key} value={p.key}>{t(`timeline.phases.${p.key}`)}</option>
               ))}
             </select>
          </div>

          <Button type="submit" className="w-full">
            {t('common.save')}
          </Button>
        </form>
      </Modal>
      
      <ConfirmDialog 
        isOpen={!!deleteId} 
        onCancel={() => setDeleteId(null)} 
        onConfirm={handleDelete} 
        title={t('common.delete')} 
        description={t('common.confirm_delete')} 
      />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}